// @ts-nocheck
/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Drawer } from '@mui/material';

import { ReactComponent as CloseIcon } from '../assets/CloseIcon.svg';

import SolyticsBox from './SolyticsBox';
import SolyticsTypography from './SolyticsTypography';
import SolyticsDivider from './SolyticsDivider';
import SolyticsIconButton from './SolyticsIconButton';

/**
 * SolyticsDrawer - A standardized right-side drawer component
 *
 * @param {boolean} open - Controls drawer visibility
 * @param {function} handleClose - Close handler for the drawer
 * @param {string|React.ReactNode} header - Main header text or node
 * @param {string|React.ReactNode} [subHeader] - Optional sub header text or node
 * @param {React.ReactNode} content - Drawer content (JSX)
 * @param {React.ReactNode} [footer] - Footer actions (JSX)
 * @param {boolean} [showDivider=true] - Show divider between header/content/footer
 * @param {object} [slotProps] - Additional props for Drawer slots
 */
const SPACING = {
  header: { px: 2, py: 2, height: '56px' },
  content: { px: 2, py: 2 },
  footer: { px: 2, py: 2, height: '64px' },
};

const SolyticsDrawer = ({
  open,
  handleClose,
  header,
  subHeader = '',
  content,
  footer,
  showDivider = true,
  slotProps = {},
  disableContentPadding = false,
  width,
  ...drawerProps
}) => {
  const resolvedDrawerWidth = width ?? { xs: '100%', sm: 520 };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: resolvedDrawerWidth,
          minWidth: resolvedDrawerWidth,
          maxWidth: '95vw',
          boxSizing: 'border-box',
          flex: '0 0 auto',
          borderRadius: 0,
        },
      }}
      slotProps={{
        ...slotProps,
        paper: {
          ...slotProps?.paper,
          sx: {
            width: resolvedDrawerWidth,
            minWidth: resolvedDrawerWidth,
            maxWidth: '95vw',
            display: 'flex',
            flexDirection: 'column',
            boxSizing: 'border-box',
            flex: '0 0 auto',
            // Override base.js global borderRadius so the side panel is flush edge-to-edge.
            borderRadius: 0,
            boxShadow: (theme) => theme.shadows[8],
            ...slotProps?.paper?.sx,
          },
        },
      }}
      {...drawerProps}
    >
      {/* Header */}
      <SolyticsBox
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ...SPACING.header,
          flexShrink: 0,
          backgroundColor: (theme) => theme?.palette?.background?.default,
        }}
      >
        <SolyticsBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            maxWidth: '80%',
          }}
        >
          {typeof header === 'string' || typeof header === 'number' ? (
            <SolyticsTypography variant="h4" color="text.primary">
              {header}
            </SolyticsTypography>
          ) : (
            header
          )}
          {!!subHeader && (
            <SolyticsTypography variant="body2" color="text.secondary">
              {subHeader}
            </SolyticsTypography>
          )}
        </SolyticsBox>
        <SolyticsIconButton
          edge="end"
          aria-label="Close drawer"
          title="Close"
          onClick={handleClose}
        >
          <CloseIcon style={{ height: '28px', width: '28px' }} />
        </SolyticsIconButton>
      </SolyticsBox>
      {showDivider && <SolyticsDivider />}
      {/* Content */}
      <SolyticsBox
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          ...SPACING.content,
          ...(disableContentPadding && { p: 0 }),
          backgroundColor: (theme) => theme?.palette?.background?.default,
        }}
      >
        {content}
      </SolyticsBox>
      {showDivider && <SolyticsDivider />}
      {/* Footer */}
      {footer && (
        <SolyticsBox
          sx={{
            gap: 1.5,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            ...SPACING.footer,
            flexShrink: 0,
          }}
        >
          {footer}
        </SolyticsBox>
      )}
    </Drawer>
  );
};

SolyticsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subHeader: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  content: PropTypes.node.isRequired,
  footer: PropTypes.node,
  showDivider: PropTypes.bool,
  width: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.object,
  ]),
};

export default SolyticsDrawer;
